import { useCallback, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import { useQuery } from "@tanstack/react-query";
import { styled, useTheme } from "styled-components";

import { formatDateAt } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import type { OpenAPI } from "simplydo/interfaces";
import { useThrottle } from "simplydo/react/hooks";
import {
  Button,
  Dropdown,
  Icon,
  Loader,
  Menu,
  Pagination,
  Popup,
  Table,
} from "simplydo/react/ui";
import { Input } from "simplydo/react/ui";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import { UserChip } from "components/lib/Chips";
import type { ChipUser } from "components/lib/Chips/types";
import ConfigurableTable from "components/lib/ConfigurableTable";
import { useTrayStatus } from "components/lib/Notifications";

import { AssessmentReportModal } from "./AssessmentReportModal";
import UserAssessmentsModal from "./UserAssessmentsModal";

const AssessmentsDashboardContainer = styled.div`
  display: flex;
  flex-direction: column;
  > .dashboard-title {
    display: block;
    margin: 0;
    font-weight: 600;
  }
  > table {
    min-width: 100%;
  }
`;

const CompletedAssessmentsInfo = styled.div<{
  $submittedAssessmentProgress?: number;
}>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  > span {
    display: block;
    margin: 0;
    color: black;
    ${({ $submittedAssessmentProgress }) =>
      $submittedAssessmentProgress === 100 && "color: green;"}
    ${({ $submittedAssessmentProgress }) =>
      $submittedAssessmentProgress === 0 && "color: red;"}
  }
  > .number {
    font-size: 1.3em;
    font-weight: 600;
  }
`;

type AssessorTableProps = {
  assessors: OpenAPI.GET<"/challenges/{id}/assessments/dashboard">["response"]["users"];
  setOpenAssessor: (assessorId: string | null) => void;
};

const AssessorTable = ({ assessors, setOpenAssessor }: AssessorTableProps) => {
  const { t } = useTranslation();
  return (
    <ConfigurableTable
      tableKey="assessmentsDashboard"
      data={assessors}
      keyExtractor={(item) => item._id}
      onRowClick={(item) => {
        setOpenAssessor(item._id);
      }}
      actions={(item) => [
        {
          name: t("assessments.dashboard.view-assessments"),
          onClick: ([assessment]) => setOpenAssessor(assessment?._id || null),
          icon: "eye",
        },
      ]}
      columns={[
        {
          key: "fullName",
          name: "Assessor",
          sortable: true,
          render: ({ item }) => (
            <UserChip compact user={item.ownerAssessor as ChipUser} />
          ),
        },
        {
          key: "totalAssessments",
          name: "Total Assessments",
          sortable: true,
          render: ({ item }) => item.totalAssessments,
        },
        {
          key: "notStartedAssessments",
          name: "Not Started",
          render: ({ item }) =>
            item.totalAssessments -
            item.totalStartedAssessments -
            item.totalSubmittedAssessments,
        },
        {
          key: "inProgressAssessment",
          name: "In Progress",
          render: ({ item }) => item.totalStartedAssessments,
        },
        {
          key: "submittedAssessments",
          name: "Submitted",
          render: ({ item }) => (
            <span>
              {item.totalSubmittedAssessments}
              {item.hasSubmissionsWithConflict ?
                <Popup
                  trigger={
                    <Icon
                      size="large"
                      color="red"
                      name="warning sign"
                      style={{ marginLeft: 5, marginTop: -4 }}
                    />
                  }
                  content={
                    "This assessor has reported a conflict of interest on one or more ideas."
                  }
                />
              : null}
            </span>
          ),
        },
        {
          key: "assessmentsCompleted",
          name: "Progress",
          sortable: true,
          render: ({ item }) => (
            <CompletedAssessmentsInfo
              $submittedAssessmentProgress={item.submittedAssessmentProgress}
            >
              <span className="number">
                {item.submittedAssessmentProgress}%
              </span>
              <span>Submitted</span>
            </CompletedAssessmentsInfo>
          ),
        },
      ]}
    />
  );
};

type IAssessmentsDashboard = {
  challenge: OpenAPI.GET<"/challenges/{id}">["response"];
};

const AssessmentsDashboard = ({ challenge }: IAssessmentsDashboard) => {
  const [_, setTrayStatus] = useTrayStatus();
  const [openAssessor, setOpenAssessor] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useState<{
    query: string;
    page: number;
    assessmentRoundId: string | null;
  }>({ query: "", page: 1, assessmentRoundId: null });
  const [assessmentReportModalOpen, setAssessmentReportModalOpen] =
    useState<boolean>(false);
  const challengeId = challenge._id;
  const user = useAppSelector((state) => state.user);
  const theme = useTheme();

  const throttleAssessments = useThrottle(
    api.challenges.getAssessmentDashboard,
    400,
    [],
  );
  const assessmentDashboardQuery = useQuery(
    initUseQuery(throttleAssessments, [challenge?._id, searchParams], {
      isEnabled: !!challenge?._id,
    }),
  );
  const assessors = assessmentDashboardQuery?.data?.users || [];
  const emails = assessmentDashboardQuery?.data?.emails || [];
  const emailsWithInvitation = emails.filter((e) => e.invite);
  const assessmentRounds = challenge?.ownerIdeaTemplate?.assessments
    ?.rounds || [{ id: "legacy", name: "Assessment" }];

  const unsubmitAssessment = useCallback(
    (ideaId: string, assessorId: string, assessmentRoundId: string) => {
      api.ideas.unsubmitAssessment(
        ideaId,
        assessmentRoundId,
        assessorId,
        () => {
          toast.success("Unsubmitted assessment");

          api.setQueryData(
            api.challenges.getAssessmentDashboard,
            [challengeId],
            (prev) => {
              return {
                ...prev,
                ideas: prev.ideas.map((idea) => {
                  if (idea._id === ideaId) {
                    return {
                      ...idea,
                      assessments:
                        idea.assessments?.map((assessment) => {
                          if (
                            assessment.user === assessorId &&
                            (assessment?.assessmentRoundId || "legacy") ===
                              assessmentRoundId
                          ) {
                            return { ...assessment, isSubmitted: false };
                          }
                          return assessment;
                        }) || [],
                    };
                  }
                  return idea;
                }),
              };
            },
          );
          api.invalidateQueries(api.challenges.getAssessmentDashboard, [
            challengeId,
          ]);
          api.invalidateQueries(api.challenges.getAssessor, [
            challengeId,
            assessorId,
          ]);
        },
        () => {
          toast.error("Unsubmission failed");
        },
      );
    },
    [challengeId],
  );

  const generateAssessmentReport = (
    assessmentRound:
      | NonNullable<
          NonNullable<OpenAPI.Schemas["IdeaTemplate"]["assessments"]>["rounds"]
        >[number]
      | null,
    includeNotes: boolean,
    selectedIdeaFields: string[],
  ) => {
    const challengeName = challenge?.name || "Challenge";

    let displayName = `${challengeName} assessment report`;
    if (assessmentRound) {
      displayName += ` - ${assessmentRound.name}`;
    }

    setAssessmentReportModalOpen(false);
    api.users.createDownload(
      user!._id,
      {
        downloadType: "assessmentXlsxExport",
        displayName: `${displayName}.xlsx`,
        challenge: challengeId,
        includeNotes,
        selectedIdeaFields,
        assessmentRoundId: assessmentRound?.id,
      },
      (download) => {
        api.setQueryData(
          api.users.getDownloads,
          [user!._id],
          (prev) => ({
            ...prev,
            pendingDownloads: (prev?.pendingDownloads || []).concat([download]),
          }),
          { allowUndefined: true },
        );
        setTrayStatus({ open: true, activeIndex: 1 });
      },
      () => {},
    );
  };

  const unassignAssessor = useCallback(
    (ideaId: string, assessorId: string, assessmentRoundId: string) => {
      util
        .confirm(
          `Unassign assessor from assessment`,
          `Are you sure you want to unassign this assessor from this assessment? Their existing assessment will be deleted. This action cannot be undone.`,
        )
        .then(() => {
          api.challenges.unassignAssessors(
            challengeId,
            [ideaId],
            [assessorId],
            assessmentRoundId,
            () => {
              setOpenAssessor("");
              api.invalidateQueries(api.challenges.getAssessmentDashboard, [
                challengeId,
              ]);
              api.invalidateQueries(api.challenges.getAssessor, [
                challengeId,
                assessorId,
              ]);
              api.invalidateQueries(api.challenges.getIdeas, [challengeId]);
            },
            (err) => {
              toast.error(err.message);
            },
          );
        })
        .catch(() => {});
    },
    [challengeId],
  );

  const resendInvitation = useCallback((invitationId: string) => {
    api.invitations.resend(
      invitationId,
      () => {
        toast.success("Invitation re-sent");
      },
      () => {
        toast.error("Failed to re-send invitation");
      },
    );
  }, []);

  const deleteInvitation = useCallback(
    (invitationId: string) => {
      api.invitations.remove(
        invitationId,
        () => {
          toast.success("Invitation deleted");

          api.setQueryData(
            api.challenges.getAssessmentDashboard,
            [challengeId],
            (prev) => ({
              ...prev,
              emails: prev.emails.filter((i) => i.invite?._id !== invitationId),
            }),
          );
        },
        () => toast.error("Unable to remove invitation"),
      );
    },
    [challengeId],
  );

  return (
    <>
      <AssessmentReportModal
        challenge={challenge}
        isOpen={assessmentReportModalOpen}
        onClose={() => setAssessmentReportModalOpen(false)}
        generateAssessmentReport={generateAssessmentReport}
      />

      <AssessmentsDashboardContainer>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 10,
          }}
        >
          <div>
            <Input
              style={{ width: !theme.sizes.isMobile ? "300px" : "224px" }}
              onChange={(_e, { value }) =>
                setSearchParams((prev) => ({ ...prev, query: value, page: 1 }))
              }
              placeholder={`Search assignments...`}
              value={searchParams.query}
              icon="search"
            />
            {openAssessor ?
              <UserAssessmentsModal
                assessorId={openAssessor}
                challenge={challenge}
                onClose={() => setOpenAssessor("")}
                unsubmitAssessment={unsubmitAssessment}
                unassignAssessor={unassignAssessor}
                assessmentRoundId={searchParams.assessmentRoundId ?? undefined}
              />
            : null}
          </div>
          <Button
            secondary
            icon="file excel"
            content="Generate report"
            style={{ margin: 0 }}
            onClick={() => setAssessmentReportModalOpen(true)}
          />
        </div>
        {assessmentRounds.length > 1 ?
          <Menu secondary>
            <Menu.Item
              onClick={() => {
                setSearchParams((prev) => ({
                  ...prev,
                  assessmentRoundId: null,
                  page: 1,
                }));
              }}
              active={!searchParams.assessmentRoundId}
            >
              All
            </Menu.Item>
            {assessmentRounds.map((round) => (
              <Menu.Item
                key={round.id}
                onClick={() => {
                  setSearchParams((prev) => ({
                    ...prev,
                    assessmentRoundId: round.id,
                    page: 1,
                  }));
                }}
                active={searchParams.assessmentRoundId === round.id}
              >
                {round.name}
              </Menu.Item>
            ))}
          </Menu>
        : null}
        {assessmentDashboardQuery?.isLoading ?
          <Loader active inline="centered" />
        : <div>
            <AssessorTable
              assessors={assessors}
              setOpenAssessor={setOpenAssessor}
            />
            {(
              assessmentDashboardQuery?.data?.nextPageAvailable ||
              assessmentDashboardQuery?.data?.previousPageAvailable
            ) ?
              <Pagination
                onPageChange={(_e, { activePage }) =>
                  setSearchParams((prev) => ({
                    ...prev,
                    page: activePage as number,
                  }))
                }
                activePage={searchParams.page}
                totalPages={Math.ceil(
                  (assessmentDashboardQuery?.data.total || 0) / 25,
                )}
                ellipsisItem={null}
                firstItem={null}
                lastItem={null}
              />
            : null}
          </div>
        }
        {emailsWithInvitation.length > 0 && (
          <>
            <h4 style={{ marginBottom: 0 }}>Pending invitations</h4>
            <Table>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell colSpan={1}>Email</Table.HeaderCell>
                  <Table.HeaderCell colSpan={1}>
                    Date of last contact
                  </Table.HeaderCell>
                  <Table.HeaderCell colSpan={2}></Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {emailsWithInvitation.map((email) => (
                  <Table.Row key={email._id}>
                    <Table.Cell collapsing>{email.email}</Table.Cell>
                    <Table.Cell>
                      {formatDateAt(
                        email?.invite?.lastContactedAt ||
                          email?.invite?.createdAt,
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="right">
                      <Dropdown
                        direction="left"
                        trigger={<Button icon="settings cog" />}
                        icon={null}
                      >
                        <Dropdown.Menu>
                          <Dropdown.Item
                            onClick={() => {
                              navigator.clipboard.writeText(
                                email.invite?.link ?? "",
                              );
                              toast.success("Link copied");
                            }}
                          >
                            <Icon name="copy" />
                            Copy invitation link
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              resendInvitation(email.invite!._id);
                            }}
                          >
                            <Icon name="mail outline" />
                            Re-send invitation
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={() => {
                              deleteInvitation(email.invite!._id);
                            }}
                          >
                            <Icon name="trash" />
                            Delete invitation
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </>
        )}
      </AssessmentsDashboardContainer>
    </>
  );
};

export default AssessmentsDashboard;
